import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  LinearProgress,
  Stack,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PasswordInput from "src/components/PasswordInput";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import SessionIcon from "@mui/icons-material/DevicesOutlined";

export interface ChangePasswordFormType {
  currentPassword?: string;
  password?: string;
  confirmPassword?: string;
}

interface ChangePasswordLayoutProps {
  onSubmit: (values: ChangePasswordFormType, actions: any) => Promise<void> | void;
}

const Schema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  password: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain an uppercase letter")
    .matches(/[a-z]/, "Password must contain a lowercase letter")
    .matches(/[0-9]/, "Password must contain a number")
    .matches(/[^A-Za-z0-9]/, "Password must contain a special character"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const ChangePasswordLayout = ({ onSubmit }: ChangePasswordLayoutProps) => {
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const { values, errors, setFieldValue, handleSubmit, isSubmitting, resetForm } =
    useFormik<ChangePasswordFormType>({
      initialValues: {
        currentPassword: "",
        password: "",
        confirmPassword: "",
      },
      validationSchema: Schema,
      validateOnChange: true,
      onSubmit: async (formValues, actions) => {
        try {
          setErrorMsg("");
          setSuccessMsg("");
          await onSubmit(formValues, actions);
          setSuccessMsg("Password updated successfully.");
          resetForm();
        } catch (err: any) {
          setErrorMsg(err.message || "Failed to update password. Please check your credentials.");
        } finally {
          actions.setSubmitting(false);
        }
      },
    });

  // Dynamic Password Strength Meter logic
  const password = values.password || "";
  const requirements = useMemo(() => {
    return [
      { id: "length", label: "At least 8 characters", test: password.length >= 8 },
      { id: "lowercase", label: "At least 1 lowercase letter", test: /[a-z]/.test(password) },
      { id: "uppercase", label: "At least 1 uppercase letter", test: /[A-Z]/.test(password) },
      { id: "number", label: "At least 1 number", test: /\d/.test(password) },
      { id: "special", label: "At least 1 special character", test: /[^A-Za-z0-9]/.test(password) },
    ];
  }, [password]);

  const strengthScore = useMemo(() => {
    if (!password) return 0;
    const passed = requirements.filter((req) => req.test).length;
    return (passed / requirements.length) * 100;
  }, [password, requirements]);

  const strengthLabel = useMemo(() => {
    if (strengthScore === 0) return { text: "None", color: "text.secondary" };
    if (strengthScore <= 40) return { text: "Weak", color: "error.main" };
    if (strengthScore <= 80) return { text: "Medium", color: "warning.main" };
    return { text: "Strong", color: "success.main" };
  }, [strengthScore]);

  return (
    <Box display="flex" flexDirection="column" gap={2.5} sx={{ maxWidth: 1120, width: "100%", mx: "auto", py: 1 }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.5, md: 3 },
          borderRadius: "20px",
          color: "#fff",
          background: "linear-gradient(135deg, #17235C 0%, #243DB7 72%, #3153D8 100%)",
          boxShadow: "0 18px 38px rgba(23,35,92,.18)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            right: -80,
            top: -120,
            bgcolor: "rgba(255,255,255,.08)",
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" position="relative" zIndex={1}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: "rgba(255,255,255,.13)",
              border: "1px solid rgba(255,255,255,.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box component="img" src="/logo.png" alt="Tyfhu" sx={{ width: 36, height: 36 }} />
          </Box>
          <Box>
            <Typography fontSize={{ xs: 22, md: 26 }} fontWeight={900} letterSpacing="-.02em">
              Change Password
            </Typography>
            <Typography fontSize={13.5} sx={{ color: "rgba(255,255,255,.76)", mt: 0.25 }}>
              Update your sign-in password and keep your admin access secure.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {successMsg && <Alert severity="success" sx={{ borderRadius: "12px" }}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ borderRadius: "12px" }}>{errorMsg}</Alert>}

      <Grid container spacing={2.5} alignItems="stretch">
        {/* Left Column: Password Update Form */}
        <Grid item xs={12} md={7.2}>
          <Card
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: "20px",
              borderColor: "#E7ECF3",
              backgroundColor: "#fff",
              boxShadow: "0 12px 32px rgba(23,35,92,.06)",
              height: "100%",
            }}
          >
            <Box mb={2.5} display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
              <Box>
                <Typography fontSize="17px" fontWeight={800}>
                  Account password
                </Typography>
                <Typography fontSize="13px" color="text.secondary" mt={0.25}>
                  Use a strong password you do not use anywhere else.
                </Typography>
              </Box>
              <Chip label={strengthLabel.text} size="small" sx={{ fontWeight: 700, color: strengthLabel.color }} />
            </Box>

            <form onSubmit={handleSubmit}>
              <Stack spacing={1.25}>
                <PasswordInput
                  label="Current Password"
                  value={values.currentPassword}
                  onChange={(val) => setFieldValue("currentPassword", val)}
                  error={errors.currentPassword}
                  size="medium"
                />

                <PasswordInput
                  label="New Password"
                  value={values.password}
                  onChange={(val) => setFieldValue("password", val)}
                  error={errors.password}
                  size="medium"
                />

                {/* Password Strength Indicator */}
                {password && (
                  <Box sx={{ mt: 0.5, p: 1.75, bgcolor: "#FBFCFE", borderRadius: "10px", border: "1px solid #EEF2F6" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography fontSize="12px" fontWeight={600} color="text.secondary">
                        Password Strength
                      </Typography>
                      <Typography fontSize="12px" fontWeight={700} color={strengthLabel.color}>
                        {strengthLabel.text}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={strengthScore}
                      color={strengthScore <= 40 ? "error" : strengthScore <= 80 ? "warning" : "success"}
                      sx={{ height: 6, borderRadius: 3, mb: 2 }}
                    />
                    <Grid container spacing={1}>
                      {requirements.map((req) => (
                        <Grid item xs={12} sm={6} key={req.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {req.test ? (
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
                          ) : (
                            <RadioButtonUncheckedIcon color="action" sx={{ fontSize: 16, opacity: 0.5 }} />
                          )}
                          <Typography fontSize="12px" fontWeight={500} color={req.test ? "text.primary" : "text.secondary"}>
                            {req.label}
                          </Typography>
                        </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <PasswordInput
                  label="Confirm New Password"
                  value={values.confirmPassword}
                  onChange={(val) => setFieldValue("confirmPassword", val)}
                  error={errors.confirmPassword}
                  size="medium"
                />

                <Box display="flex" justifyContent="flex-end" pt={0.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      px: 4,
                      minHeight: 50,
                      boxShadow: "0 10px 22px rgba(36,61,183,.18)",
                    }}
                  >
                    {isSubmitting ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} color="inherit" />
                        <span>Updating Password...</span>
                      </Box>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Card>
        </Grid>

        <Grid item xs={12} md={4.8}>
          <Card
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: "20px",
              borderColor: "#E7ECF3",
              boxShadow: "0 12px 32px rgba(23,35,92,.06)",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
              height: "100%",
            }}
          >
            <Stack spacing={2.25}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "12px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid #EEF2F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                }}
              >
                <ShieldIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography fontSize="16px" fontWeight={800}>
                  Security checklist
                </Typography>
                <Typography fontSize="13px" color="text.secondary" mt={0.5} lineHeight={1.6}>
                  A stronger password protects your admin console, company records, reports, and store operations.
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                {[
                  { icon: <KeyIcon />, title: "Use a unique password", text: "Avoid reusing passwords from email, banking, or shared tools." },
                  { icon: <ShieldIcon />, title: "Meet all strength rules", text: "Uppercase, lowercase, number, special character, and enough length." },
                  { icon: <SessionIcon />, title: "You will sign in again", text: "After updating, your current session is cleared for safety." },
                ].map((item) => (
                  <Box key={item.title} display="flex" gap={1.25} alignItems="flex-start">
                    <Box sx={{ color: "text.secondary", display: "flex", mt: 0.25, "& svg": { fontSize: 18 } }}>
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography fontSize="13.5px" fontWeight={700}>{item.title}</Typography>
                      <Typography fontSize="12.5px" color="text.secondary" lineHeight={1.5}>{item.text}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default ChangePasswordLayout;
